<template>
  <section class="ead-page">
    <titlebar :title-stack="['Detalhes do progresso']" />
    <b-loading :active="loading" />

    <template v-if="dados.resumo">
      <header class="detalhe-header box">
        <div>
          <p class="detalhe-header__legenda">Progresso individual</p>
          <h1 class="title is-3 mb-1">{{ dados.resumo.funcionario }}</h1>
          <p class="subtitle is-6 mb-0 mt-2">{{ dados.resumo.curso }}</p>
        </div>
        <router-link class="button" :to="{ name: 'ead-relatorio' }">
          <b-icon icon="arrow-left" size="is-small" />
          <span>Voltar ao relatório</span>
        </router-link>
      </header>

      <div class="resumo-grid">
        <article class="box resumo-card">
          <span class="resumo-card__rotulo">Progresso</span>
          <ead-progresso :valor="dados.resumo.progresso" />
          <span class="is-size-7 has-text-grey">
            {{ dados.resumo.aulasConcluidas }} de {{ dados.resumo.totalAulas }} aulas
          </span>
        </article>
        <article class="box resumo-card">
          <span class="resumo-card__rotulo">Status</span>
          <b-tag :type="tipoStatus(dados.resumo.status)" size="is-medium">
            {{ formatarStatus(dados.resumo.status) }}
          </b-tag>
        </article>
        <article class="box resumo-card">
          <span class="resumo-card__rotulo">Nota final</span>
          <strong class="resumo-card__valor">
            {{ dados.resumo.notaFinal == null ? '—' : dados.resumo.notaFinal }}
          </strong>
        </article>
        <article class="box resumo-card">
          <span class="resumo-card__rotulo">Conclusão</span>
          <strong>{{ formatarData(dados.resumo.dataConclusao) }}</strong>
        </article>
      </div>

      <div class="secao-titulo">
        <div>
          <h2 class="title is-4 mb-1">Aulas e respostas</h2>
          <p class="has-text-grey is-size-7">
            Situação de cada aula e respostas registradas pelo aluno.
          </p>
        </div>
      </div>

      <b-message v-if="!dados.modulos || !dados.modulos.length" type="is-info">
        O curso não possui módulos cadastrados.
      </b-message>

      <article v-for="modulo in dados.modulos" :key="modulo.id" class="modulo-card">
        <header class="modulo-header">
          <div class="identificacao">
            <span class="ordem-badge">{{ modulo.ordem }}</span>
            <div>
              <div class="titulo-status">
                <h3 class="title is-5 mb-0">{{ modulo.nome }}</h3>
                <b-tag v-if="!modulo.ativo" type="is-light">Módulo inativo</b-tag>
              </div>
              <p v-if="modulo.descricao" class="has-text-grey is-size-7 mt-1">
                {{ modulo.descricao }}
              </p>
            </div>
          </div>
        </header>

        <section v-for="aula in modulo.aulas" :key="aula.id" class="aula-card">
          <header class="aula-header">
            <div class="identificacao">
              <b-icon
                :icon="aula.concluida ? 'check-circle' : aula.iniciada ? 'progress-clock' : 'circle-outline'"
                :type="aula.concluida ? 'is-success' : aula.iniciada ? 'is-warning' : 'is-light'"
              />
              <div>
                <div class="titulo-status">
                  <h4 class="has-text-weight-semibold">{{ aula.ordem }}. {{ aula.nome }}</h4>
                  <b-tag :type="aula.concluida ? 'is-success' : aula.iniciada ? 'is-warning' : 'is-light'">
                    {{ aula.concluida ? 'Concluída' : aula.iniciada ? 'Iniciada' : 'Não iniciada' }}
                  </b-tag>
                  <b-tag v-if="!aula.ativo" type="is-light">Aula inativa</b-tag>
                </div>
                <p v-if="aula.dataInicio" class="is-size-7 has-text-grey mt-1">
                  Início: {{ formatarData(aula.dataInicio) }}
                  <span v-if="aula.dataConclusao">
                    · Conclusão: {{ formatarData(aula.dataConclusao) }}
                  </span>
                </p>
              </div>
            </div>
          </header>

          <div v-if="aula.perguntas && aula.perguntas.length" class="perguntas-aula">
            <div class="perguntas-titulo">
              <span class="has-text-weight-semibold">Perguntas da aula</span>
              <b-tag rounded>{{ aula.perguntas.length }}</b-tag>
            </div>
            <div v-for="pergunta in aula.perguntas" :key="pergunta.id" class="pergunta-item">
              <span class="pergunta-ordem">{{ pergunta.ordem }}</span>
              <div class="pergunta-conteudo">
                <div class="titulo-status">
                  <p class="has-text-weight-semibold">{{ pergunta.enunciado }}</p>
                  <b-tag v-if="!pergunta.ativo" type="is-light" size="is-small">Inativa</b-tag>
                </div>
                <p class="resposta-label">Resposta do aluno</p>
                <div :class="['resposta-box', classeResposta(pergunta.correta)]">
                  <b-icon :icon="iconeResposta(pergunta)" size="is-small" />
                  <span>{{ textoResposta(pergunta) }}</span>
                  <b-tag
                    v-if="pergunta.correta != null"
                    :type="pergunta.correta ? 'is-success' : 'is-danger'"
                    size="is-small"
                  >
                    {{ pergunta.correta ? 'Correta' : 'Incorreta' }}
                  </b-tag>
                </div>
                <p v-if="pergunta.dataResposta" class="is-size-7 has-text-grey mt-1">
                  Respondida em {{ formatarData(pergunta.dataResposta) }}
                </p>
              </div>
            </div>
          </div>
        </section>
      </article>

      <section class="avaliacao-secao">
        <div class="secao-titulo">
          <div>
            <h2 class="title is-4 mb-1">Respostas da avaliação</h2>
            <p class="has-text-grey is-size-7">
              Histórico de tentativas e respostas enviadas pelo aluno.
            </p>
          </div>
        </div>

        <b-message v-if="!dados.avaliacao" type="is-info">
          Este curso não possui avaliação cadastrada.
        </b-message>
        <b-message
          v-else-if="!dados.avaliacao.tentativas || !dados.avaliacao.tentativas.length"
          type="is-info"
        >
          O aluno ainda não realizou nenhuma tentativa da avaliação.
        </b-message>

        <article
          v-for="tentativa in (dados.avaliacao && dados.avaliacao.tentativas) || []"
          :key="tentativa.id"
          class="tentativa-card box"
        >
          <header class="tentativa-header">
            <div>
              <h3 class="title is-5 mb-1">Tentativa {{ tentativa.numeroTentativa }}</h3>
              <p class="is-size-7 has-text-grey">
                Iniciada em {{ formatarData(tentativa.dataInicio) }}
                <span v-if="tentativa.dataFim"> · Finalizada em {{ formatarData(tentativa.dataFim) }}</span>
              </p>
            </div>
            <div class="tentativa-resultado">
              <b-tag v-if="tentativa.dataFim" :type="tentativa.aprovado ? 'is-success' : 'is-danger'">
                {{ tentativa.aprovado ? 'Aprovado' : 'Reprovado' }}
              </b-tag>
              <b-tag v-else type="is-warning">Em andamento</b-tag>
              <strong>Nota: {{ tentativa.nota == null ? '—' : tentativa.nota }}</strong>
              <span class="is-size-7 has-text-grey">Mínima: {{ tentativa.notaMinimaAplicada }}</span>
            </div>
          </header>

          <div v-if="tentativa.respostas && tentativa.respostas.length" class="tentativa-respostas">
            <div v-for="resposta in tentativa.respostas" :key="resposta.perguntaId" class="resposta-avaliacao">
              <span class="pergunta-ordem">{{ resposta.ordem }}</span>
              <div>
                <p class="has-text-weight-semibold">{{ resposta.enunciado }}</p>
                <p :class="resposta.correta ? 'has-text-success' : 'has-text-danger'">
                  <b-icon :icon="resposta.correta ? 'check-circle' : 'close-circle'" size="is-small" />
                  {{ resposta.alternativaDescricao }}
                </p>
              </div>
            </div>
          </div>
          <p v-else class="has-text-grey is-size-7 mt-3">
            Esta tentativa ainda não possui respostas finalizadas.
          </p>
        </article>
      </section>
    </template>
  </section>
</template>

<script>
import http from '@/http'
import titlebar from '@/components/titlebar.vue'
import EadProgresso from '@/components/ead/ead-progresso.vue'

export default {
  name: 'EadProgressoDetalhe',
  components: { titlebar, EadProgresso },
  data: () => ({
    dados: {},
    loading: false,
  }),
  mounted() {
    this.carregar()
  },
  methods: {
    carregar() {
      this.loading = true
      http
        .get(`/api/ead-relatorios/progresso/${this.$route.params.id}`)
        .then((r) => (this.dados = r.data))
        .finally(() => (this.loading = false))
    },
    formatarData(valor) {
      if (!valor) return '—'
      return new Date(valor).toLocaleString('pt-BR')
    },
    formatarStatus(status) {
      return (status || '').split('_').join(' ')
    },
    tipoStatus(status) {
      if (status === 'CONCLUIDO') return 'is-success'
      if (status === 'REPROVADO' || status === 'BLOQUEADO') return 'is-danger'
      if (status === 'EM_ANDAMENTO') return 'is-warning'
      return 'is-light'
    },
    textoResposta(pergunta) {
      return pergunta.respostaTexto || pergunta.alternativaDescricao || 'Não respondida'
    },
    iconeResposta(pergunta) {
      if (!pergunta.respostaTexto && !pergunta.alternativaDescricao) return 'minus-circle-outline'
      if (pergunta.correta == null) return 'comment-text-outline'
      return pergunta.correta ? 'check-circle' : 'close-circle'
    },
    classeResposta(correta) {
      if (correta == null) return ''
      return correta ? 'resposta-box--correta' : 'resposta-box--incorreta'
    },
  },
}
</script>

<style scoped>
.detalhe-header,
.identificacao,
.titulo-status,
.perguntas-titulo,
.tentativa-header,
.tentativa-resultado,
.resposta-box,
.resposta-avaliacao {
  align-items: center;
  display: flex;
}

.detalhe-header,
.perguntas-titulo,
.tentativa-header {
  justify-content: space-between;
  gap: 1rem;
}

.detalhe-header {
  border-left: 4px solid #00b89c;
}

.detalhe-header__legenda,
.resumo-card__rotulo,
.resposta-label {
  color: #7a7a7a;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.resumo-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 2rem;
}

.resumo-card {
  margin-bottom: 0;
}

.resumo-card__rotulo {
  display: block;
  margin-bottom: 0.6rem;
}

.resumo-card__valor {
  display: block;
  font-size: 1.4rem;
}

.secao-titulo {
  margin: 2rem 0 1rem;
}

.modulo-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 1.25rem;
  overflow: hidden;
}

.modulo-header {
  background: #fafafa;
  border-bottom: 1px solid #ededed;
  padding: 1rem 1.25rem;
}

.identificacao,
.titulo-status,
.tentativa-resultado,
.resposta-box,
.resposta-avaliacao {
  gap: 0.65rem;
}

.titulo-status {
  flex-wrap: wrap;
}

.ordem-badge,
.pergunta-ordem {
  align-items: center;
  background: #f0f0f0;
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 auto;
  font-weight: 700;
  height: 1.9rem;
  justify-content: center;
  width: 1.9rem;
}

.ordem-badge {
  background: #00b89c;
  color: #fff;
  height: 2.25rem;
  width: 2.25rem;
}

.aula-card {
  border: 1px solid #ededed;
  border-radius: 7px;
  margin: 1rem;
  overflow: hidden;
}

.aula-header {
  padding: 0.9rem 1rem;
}

.perguntas-aula {
  background: #fafafa;
  border-top: 1px solid #ededed;
  padding: 0.85rem 1rem;
}

.perguntas-titulo {
  margin-bottom: 0.65rem;
}

.pergunta-item {
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 6px;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: auto minmax(0, 1fr);
  padding: 0.8rem;
}

.pergunta-item + .pergunta-item,
.resposta-avaliacao + .resposta-avaliacao {
  margin-top: 0.6rem;
}

.pergunta-conteudo {
  min-width: 0;
}

.resposta-label {
  margin-top: 0.7rem;
}

.resposta-box {
  background: #f5f5f5;
  border-radius: 5px;
  margin-top: 0.25rem;
  padding: 0.55rem 0.7rem;
}

.resposta-box--correta {
  background: #effaf5;
  color: #257953;
}

.resposta-box--incorreta {
  background: #feecf0;
  color: #cc0f35;
}

.avaliacao-secao {
  margin-bottom: 2rem;
}

.tentativa-header {
  border-bottom: 1px solid #ededed;
  padding-bottom: 0.8rem;
}

.tentativa-resultado {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.tentativa-respostas {
  margin-top: 1rem;
}

.resposta-avaliacao {
  align-items: flex-start;
  background: #fafafa;
  border-radius: 6px;
  padding: 0.75rem;
}

@media screen and (max-width: 900px) {
  .resumo-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 600px) {
  .detalhe-header,
  .tentativa-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .detalhe-header .button {
    width: 100%;
  }

  .resumo-grid {
    grid-template-columns: 1fr;
  }

  .tentativa-resultado {
    justify-content: flex-start;
  }
}
</style>
